/**
 * src/domain/media/oembed-service.ts
 *
 * Client service to fetch and cache normalized oEmbed metadata for video links.
 * Features:
 *  - 24-hour localStorage caching with memory fallback
 *  - Primary fetch via backend proxy (/api/v1/media/embed/metadata) to bypass CORS
 *  - Client fallback to direct oEmbed or deterministic poster generation
 */

import { MediaClassifier } from './media-classifier.js';

export interface OEmbedMetadata {
  title: string;
  authorName: string;
  authorUrl?: string;
  thumbnailUrl: string;
  provider: 'youtube' | 'vimeo' | 'unknown';
  aspectRatio: '16:9' | '4:3';
  durationSec?: number;
  isLive?: boolean;
}

const CACHE_PREFIX = 'herx_oembed_cache_v1_';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export class OEmbedService {
  private static memoryCache = new Map<string, { data: OEmbedMetadata; expiresAt: number }>();

  /**
   * Fetches metadata for a given video URL with caching.
   */
  public static async fetchMetadata(url: string): Promise<OEmbedMetadata> {
    const cleanUrl = url.trim();
    const cacheKey = CACHE_PREFIX + cleanUrl;

    // 1. Check Memory Cache
    const memCached = this.memoryCache.get(cleanUrl);
    if (memCached && Date.now() < memCached.expiresAt) {
      return memCached.data;
    }

    // 2. Check LocalStorage Cache
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem(cacheKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Date.now() < parsed.expiresAt) {
            this.memoryCache.set(cleanUrl, parsed);
            return parsed.data;
          }
        }
      }
    } catch {}

    // 3. Classify input
    const classification = MediaClassifier.classify(cleanUrl);

    // 4. Try backend proxy
    try {
      const resp = await fetch(`/api/v1/media/embed/metadata?url=${encodeURIComponent(cleanUrl)}`, {
        headers: { Accept: 'application/json' },
      });
      if (resp.ok) {
        const payload = await resp.json();
        const metadata: OEmbedMetadata = {
          title: payload.title || classification.suggestedTitle || 'Video Session',
          authorName: payload.author_name || payload.authorName || 'HERX Creator',
          authorUrl: payload.author_url || payload.authorUrl,
          thumbnailUrl: payload.thumbnail_url || payload.thumbnailUrl || this.getFallbackThumbnail(classification),
          provider: classification.meta.provider || 'youtube',
          aspectRatio: '16:9',
          durationSec: payload.duration || 0,
          isLive: classification.meta.isLive || false,
        };

        this.setCache(cleanUrl, metadata);
        return metadata;
      }
    } catch {
      // Backend unreachable, proceed to fallback
    }

    // 5. Offline / Client-Side Fallback Generation
    const fallbackMeta: OEmbedMetadata = {
      title: classification.suggestedTitle || 'Video Session',
      authorName: 'Sovereign Creator',
      thumbnailUrl: this.getFallbackThumbnail(classification),
      provider: classification.meta.provider || 'unknown',
      aspectRatio: '16:9',
      isLive: classification.meta.isLive || false,
    };

    this.setCache(cleanUrl, fallbackMeta);
    return fallbackMeta;
  }

  private static getFallbackThumbnail(classification: ReturnType<typeof MediaClassifier.classify>): string {
    if (classification.meta.provider === 'youtube' && classification.meta.platformId) {
      return `https://img.youtube.com/vi/${classification.meta.platformId}/maxresdefault.jpg`;
    }
    return 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/TearsOfSteel.jpg';
  }

  private static setCache(url: string, data: OEmbedMetadata) {
    const entry = { data, expiresAt: Date.now() + CACHE_TTL_MS };
    this.memoryCache.set(url, entry);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(CACHE_PREFIX + url, JSON.stringify(entry));
      }
    } catch {}
  }
}
